/**
 * LA PASTILLE « COMPTE DE CONNEXION » D'UNE CARTE DE L'ORGANIGRAMME — module PUR (03/10/2026).
 *
 * Demande RQ : voir d'un coup d'œil, sur l'organigramme, quel profil est relié à
 * un compte, et dans quel état. L'état est CALCULÉ par le moteur contre
 * `up_users` (`backend/src/etat-compte.ts`) et servi dans `gedUsers[].etatCompte`
 * aux seuls administrateurs de comptes ou de dossiers.
 *
 * ⚠️ TROIS CAS À NE PAS CONFONDRE :
 *  · `etatCompte` ABSENT → l'état n'a pas été calculé (lecteur non autorisé,
 *    moteur antérieur) : AUCUNE pastille — on n'affirme rien qu'on n'a pas lu ;
 *  · `aucun` → VÉRIFIÉ sans compte : pastille neutre, creuse. Plus de la moitié
 *    du personnel n'a pas de compte et ce n'est pas une anomalie : la pastille
 *    ne doit pas avoir l'air d'une alerte ;
 *  · un état réel → pastille pleine, couleur de l'état, mêmes libellés que la
 *    fiche (« Compte actif », « Invitation en attente »…).
 *
 * ⚠️ On ne lit PAS l'ancien champ `compte` (miroir persisté de `statutCompte`) :
 * il est NULL sur toutes les fiches en base, il aurait dit « sans compte » des
 * dix personnes qui se connectent chaque jour.
 */

export type EtatCompteVue = 'actif' | 'invite' | 'expiree' | 'suspendu' | 'archive' | 'aucun';

export interface Pastille {
  libelle: string;
  /** couleur du rond (et de son contour) */
  couleur: string;
  /** `false` = rond creux (sans compte) */
  plein: boolean;
}

const PASTILLES: Record<EtatCompteVue, Pastille> = {
  actif: { libelle: 'Compte actif', couleur: 'var(--green-600)', plein: true },
  invite: { libelle: 'Invitation en attente', couleur: 'var(--amber-600)', plein: true },
  expiree: { libelle: 'Invitation expirée', couleur: 'var(--red-600)', plein: true },
  suspendu: { libelle: 'Compte suspendu', couleur: 'var(--amber-600)', plein: true },
  archive: { libelle: 'Compte archivé', couleur: 'var(--text-muted)', plein: true },
  aucun: { libelle: 'Sans compte de connexion', couleur: 'var(--text-muted)', plein: false },
};

/** La pastille d'une carte, ou `null` quand il n'y a RIEN à affirmer. */
export function pastilleCompte(etat: unknown): Pastille | null {
  if (typeof etat !== 'string' || !(etat in PASTILLES)) return null;
  return PASTILLES[etat as EtatCompteVue];
}

/** L'infobulle : l'état, puis ce que le clic ouvre. */
export function titrePastille(etat: unknown, nom: string): string {
  const p = pastilleCompte(etat);
  if (!p) return '';
  const geste = etat === 'aucun'
    ? 'ouvrir le dossier pour inviter la personne ou lui relier un compte'
    : 'ouvrir les paramètres du compte dans le dossier';
  return `${p.libelle}${nom ? ` — ${nom}` : ''} : ${geste}`;
}
